import {useTranslation} from '@aboutyou/i18n';
import {styled} from '@linaria/react';
import React from 'react';
import {COLORS} from '../../../constants/colors';
import {SPACING} from '../../../constants/sizing';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {UnstyledButton} from '../Buttons/UnstyledButton';
import {Icon} from '../Icon/Icon';
import {GenericIconName} from '../Icon/IconName';

interface ContentPopperHeaderProps {
  className?: string;
  ['data-testid']?: string;
  tabIndex?: number;
  onGoBack?: VoidFunction;
  onClose?: (e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => void;
}

export const ContentPopperHeader: React.FC<
  React.PropsWithChildren<ContentPopperHeaderProps>
> = ({
  className,
  children,
  'data-testid': dataTestId,
  onGoBack,
  onClose,
  tabIndex,
}) => {
  const {t} = useTranslation();
  const isCloseIconShown = Boolean(onClose && !onGoBack);

  return (
    <Header
      className={className}
      data-testid={`${dataTestId}Header`}
      role="button"
      tabIndex={tabIndex}
      $isCloseIconShown={isCloseIconShown}
      $isGoBackIconShown={!!onGoBack}
    >
      {onGoBack && (
        <UnstyledButton
          data-testid={`${dataTestId}GoBack`}
          onClick={onGoBack}
          aria-label={t('Go back')}
        >
          <Icon
            asset={GenericIconName.ArrowLeft}
            size="S"
            fill={COLORS.Black}
          />
        </UnstyledButton>
      )}
      <div>{children}</div>
      {isCloseIconShown && (
        <UnstyledButton
          data-testid={`${dataTestId}Close`}
          onClick={onClose}
          aria-label={t('Go back')}
        >
          <Icon asset={GenericIconName.Close} size="S" fill={COLORS.Black} />
        </UnstyledButton>
      )}
    </Header>
  );
};

const Header = styled.header<{
  $isCloseIconShown?: boolean;
  $isGoBackIconShown?: boolean;
}>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACING['X.Lg']};

  padding: ${props => {
    if (props.$isCloseIconShown) {
      return `${SPACING['Y.Lg']} ${SPACING['X.Lg']} ${SPACING['Y.Lg']} ${SPACING['X.Xl4']}`;
    }

    if (props.$isGoBackIconShown) {
      return `${SPACING['Y.Lg']} ${SPACING['X.Xl4']} ${SPACING['Y.Lg']} ${SPACING['X.Lg']}`;
    }

    return `${SPACING['Y.Lg']} ${SPACING['X.Lg']}`;
  }};

  flex-shrink: 0;
  overscroll-behavior: contain;

  > div {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;

    font-size: ${FONT_SIZES.px20};
    font-weight: ${FONT_WEIGHTS.mediumHeavy};

    > span {
      font-size: ${FONT_SIZES.px20};
      font-weight: ${FONT_WEIGHTS.mediumHeavy};
    }
  }

  &:focus {
    outline: none;
  }
`;
